import apiClient from '@/lib/api-client';
import { transformBookingNoteSummary } from './sessions.service';
import type {
  Customer,
  CustomerListResponse,
  CustomerCreateInput,
  CustomerUpdateInput,
  CustomerBooking
} from '@/types/customer';

interface BackendCustomer {
  id: number;
  public_id?: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone?: string;
  notes?: string;
  total_visits: number;
  last_visit_at?: string;
  created_at: string;
  updated_at: string;
  bookings?: BackendCustomerBooking[];
}

interface BackendCustomerBooking {
  id: number;
  public_id?: string;
  booking_code?: string;
  status: string;
  service_name?: string;
  provider_name?: string;
  start_time?: string;
  created_at: string;
  session_note?: unknown;
}

interface BackendMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

export type CustomerFilter = 'all' | 'with_bookings' | 'with_completed_bookings' | 'without_bookings' | 'recent';

export interface CustomerListParams {
  search?: string;
  page?: number;
  perPage?: number;
  sortBy?: 'last_visit_at';
  sortDir?: 'asc' | 'desc';
  filter?: CustomerFilter;
}

function transformCustomer(backend: BackendCustomer): Customer {
  return {
    id: backend.id,
    publicId: backend.public_id,
    public_id: backend.public_id,
    firstName: backend.first_name,
    lastName: backend.last_name,
    fullName: backend.full_name,
    email: backend.email,
    phone: backend.phone,
    notes: backend.notes,
    totalVisits: backend.total_visits,
    lastVisitAt: backend.last_visit_at,
    createdAt: backend.created_at,
    updatedAt: backend.updated_at,
    bookings: backend.bookings?.map(transformBooking)
  };
}

function transformBooking(backend: BackendCustomerBooking): CustomerBooking {
  return {
    id: backend.id,
    publicId: backend.public_id,
    public_id: backend.public_id,
    bookingCode: backend.booking_code,
    booking_code: backend.booking_code,
    status: backend.status,
    serviceName: backend.service_name,
    providerName: backend.provider_name,
    startTime: backend.start_time,
    createdAt: backend.created_at,
    sessionNote: transformBookingNoteSummary(backend.session_note),
  };
}

function transformMeta(backend: BackendMeta): CustomerListResponse['meta'] {
  return {
    currentPage: backend.current_page,
    totalPages: backend.total_pages,
    totalCount: backend.total_count,
    perPage: backend.per_page
  };
}

function toBackendPayload(input: CustomerCreateInput | CustomerUpdateInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if ('firstName' in input && input.firstName !== undefined) {
    payload.first_name = input.firstName;
  }
  if ('lastName' in input && input.lastName !== undefined) {
    payload.last_name = input.lastName;
  }
  if ('email' in input && input.email !== undefined) {
    payload.email = input.email;
  }
  if ('phone' in input) {
    payload.phone = input.phone;
  }
  if ('notes' in input) {
    payload.notes = input.notes;
  }

  return payload;
}

class CustomerService {
  async list(params: CustomerListParams = {}): Promise<CustomerListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? 1,
      per_page: params.perPage ?? 20
    };
    if (params.search?.trim()) queryParams.search = params.search.trim();
    if (params.sortBy) queryParams.sort_by = params.sortBy;
    if (params.sortDir) queryParams.sort_dir = params.sortDir;
    if (params.filter && params.filter !== 'all') queryParams.filter = params.filter;

    const { data } = await apiClient.get<{ data: { customers: BackendCustomer[]; meta: BackendMeta } }>(
      '/v1/admin/customers',
      { params: queryParams }
    );

    return {
      customers: data.data.customers.map(transformCustomer),
      meta: transformMeta(data.data.meta)
    };
  }

  async getCustomer(id: number | string): Promise<Customer> {
    const { data } = await apiClient.get<{ data: { customer: BackendCustomer } }>(
      `/v1/admin/customers/${id}`
    );

    return transformCustomer(data.data.customer);
  }

  async createCustomer(input: CustomerCreateInput): Promise<Customer> {
    const { data } = await apiClient.post<{ data: { customer: BackendCustomer } }>(
      '/v1/admin/customers',
      { customer: toBackendPayload(input) }
    );

    return transformCustomer(data.data.customer);
  }

  async updateCustomer(id: number | string, input: CustomerUpdateInput): Promise<Customer> {
    const { data } = await apiClient.patch<{ data: { customer: BackendCustomer } }>(
      `/v1/admin/customers/${id}`,
      { customer: toBackendPayload(input) }
    );

    return transformCustomer(data.data.customer);
  }

  async deleteCustomer(id: number | string): Promise<void> {
    await apiClient.delete(`/v1/admin/customers/${id}`);
  }
}

export const customerService = new CustomerService();
